import type {DurationLike} from 'luxon'

import {
  Trademark_Class,
  Use_In_Commerce_Request,
  UseInCommerceRequestFragment,
} from '@/utils/graphqlSdk'
import {Service} from '@/utils/service-request/types'

import {UseInCommerceRequestDocumentType} from '../document/useInCommerceRequestDocumentType'
import {UseInCommerceRequestNotification} from '../notifications/emails/entities/use-in-commerce-request/constants'

export const GRACE_PERIOD_DURATION: DurationLike = {months: 6}

export const RENEWAL_FILING_WINDOW: DurationLike = {years: 1}

export const US_RENEWAL_TERM: DurationLike = {years: 10}

export const DOU_FILING_WINDOW_START: DurationLike = {years: 5}

export const DOU_FILING_WINDOW_END: DurationLike = {years: 6}

export const REQUEST_CREATION_CATCH_UP: DurationLike = {days: 7}

export const UIC_ISSUE_DATE_TIMEZONE = 'America/New_York'

export enum UseInCommerceRequestAction {
  visitedLandingPage = 'Landing page visited',
  visitedPaymentForm = 'Payment form visited',
  invoicePaid = 'Invoice paid',
  orderCompleted = 'Order completed',
  sevenDayDeadlineConfirmed = '7-day deadline confirmed',
  twoDayDeadlineConfirmed = '2-day deadline confirmed',
  divided = 'Divided',
  reopened = 'Reopened',
  applicationNotSubmittedReminder = 'Application not submitted reminder',
  urgingSmsSent = 'Urging sms sent',
  landingPageNotVisited1st = 'Landing page not visited 1st',
  landingPageNotVisited2nd = 'Landing page not visited 2nd',
  landingPageNotVisited3rd = 'Landing page not visited 3rd',
  landingPageNotVisited4th = 'Landing page not visited 4th',
  landingPageNotVisited5th = 'Landing page not visited 5th',
  notSubmitted1st = 'Not submitted 1st',
  notSubmitted2nd = 'Not submitted 2nd',
  notSubmitted3rd = 'Not submitted 3rd',
  notSubmitted4th = 'Not submitted 4th',
  reviewComments1st = 'Review comments 1st',
  reviewComments2nd = 'Review comments 2nd',
  reviewComments3rd = 'Review comments 3rd',
  reviewDeadlineReminder = 'Review deadline reminder',
  completeOrder1st = 'Complete order 1st',
  completeOrder2nd = 'Complete order 2nd',
  completeOrder3rd = 'Complete order 3rd',
  sevenDayGracePeriodDeadlineConfirmed = '7-day grace period deadline confirmed',
  twoDayGracePeriodDeadlineConfirmed = '2-day grace period deadline confirmed',
}

export enum UseInCommerceRequestStatus {
  created = 'Created',
  prepareKickOff = 'Kick-off preparation',
  sentKickOff = 'Kick-off sent',
  checkoutCompleted = 'Checkout completed',
  readyForReview = 'Ready for review',
  sentToClient = 'Sent to client',
  reviewRequested = 'Review requested',
  filingApproved = 'Approved for filing',
  extensionSubmitted = 'Extension submitted',
  souSubmitted = 'Statement of Use submitted',
  postNoaAmendmentSubmitted = 'Post-NOA amendment submitted',
  abandonedByClient = 'Abandoned by client',
  abandonedAutomatically = 'Abandoned automatically',
  abandonmentConfirmed = 'Abandonment confirmed',
  done = 'Done',
  closed = 'Closed',
  douSubmitted = 'Declaration of Use submitted',
  onHold = 'On hold',
  sentForSignature = 'Sent for signature',
  renewalSubmitted = 'Renewal submitted',
}

export const useInCommercePreSubmitStatuses = [
  UseInCommerceRequestStatus.created,
  UseInCommerceRequestStatus.prepareKickOff,
  UseInCommerceRequestStatus.sentKickOff,
]

export const useInCommerceRequestFinalStatuses = [
  UseInCommerceRequestStatus.souSubmitted,
  UseInCommerceRequestStatus.postNoaAmendmentSubmitted,
  UseInCommerceRequestStatus.extensionSubmitted,
  UseInCommerceRequestStatus.abandonedByClient,
  UseInCommerceRequestStatus.abandonmentConfirmed,
  UseInCommerceRequestStatus.done,
  UseInCommerceRequestStatus.closed,
  UseInCommerceRequestStatus.douSubmitted,
  UseInCommerceRequestStatus.renewalSubmitted,
]

export const useInCommerceRequestAbandonedStatuses = [
  UseInCommerceRequestStatus.abandonedAutomatically,
  UseInCommerceRequestStatus.abandonmentConfirmed,
  UseInCommerceRequestStatus.abandonedByClient,
]

export enum UseInCommerceRequestType {
  statementOfUse = 'Statement of Use',
  declarationOfUse = 'Declaration of Use',
  renewal = 'Renewal',
}

export enum UseInCommerceRequestClassDecision {
  specimen = 'specimen',
  extension = 'extension',
  abandon = 'abandon',
}

export enum UseInCommerceRequestSubmission {
  specimen = 'specimen',
  extension = 'extension',
  divide = 'divide',
  abandon = 'abandon',
}

export const useInCommerceRequestEditableProps: Array<keyof Use_In_Commerce_Request> = [
  'kickoff_type',
  'remaining_extension_count',
  'issue_date',
  'grace_period_deadline',
]

export const useInCommerceRequestClassEditableProps: Array<keyof Trademark_Class> = [
  'decision',
  'websites',
  'class_items',
  'filing_basis',
  'first_use_in_us',
  'first_use_anywhere',
]

export enum UseInCommerceRequestSectionCommentThreadID {
  overview = 'overview',
  classes = 'classes',
}

export const useInCommerceRequestTypeToService: Record<UseInCommerceRequestType, Service> = {
  [UseInCommerceRequestType.statementOfUse]: Service.statementOfUse,
  [UseInCommerceRequestType.declarationOfUse]: Service.declarationOfUse,
  [UseInCommerceRequestType.renewal]: Service.tmRenewal,
}

export enum UseInCommerceRequestKickOffType {
  standard = 'Standard client kick-off',
  noPayment = 'No payment required kick-off',
  // external = 'External client kick-off',
  // partner = 'Partner kick-off',
}

export enum UseInCommercePriceQuoteType {
  SOU = 'sou',
  DOU = 'dou',
}

export type UseInCommerceRequestStatusToNotification = Partial<
  Record<
    UseInCommerceRequestStatus | UseInCommerceRequestAction,
    | UseInCommerceRequestNotification
    | ((
        useInCommerceRequest: UseInCommerceRequestFragment,
      ) => Promise<UseInCommerceRequestNotification>)
  >
>

export const uicSendToClientNotificationMap: Record<
  UseInCommerceRequestType,
  UseInCommerceRequestNotification
> = {
  [UseInCommerceRequestType.statementOfUse]:
    UseInCommerceRequestNotification.souReviewCommentsFromLawyer,
  [UseInCommerceRequestType.declarationOfUse]:
    UseInCommerceRequestNotification.douDraftSendForApproval,
  [UseInCommerceRequestType.renewal]: UseInCommerceRequestNotification.douDraftSendForApproval,
}

export enum UseInCommerceRequestTrackingActionId {
  prepareKickOff = 'prepareKickOff',
  sendKickOff = 'sendKickOff',
  paidElsewhere = 'paidElsewhere',
  markAsPaid = 'markAsPaid',
  readyForReview = 'readyForReview',
  sentToClient = 'sentToClient',
  reviewRequested = 'reviewRequested',
  filingApproved = 'filingApproved',
  markAsFiled = 'markAsFiled',
  abandonmentConfirmed = 'abandonmentConfirmed',
  cancelAbandonment = 'cancelAbandonment',
  revertConfirmedAbandonment = 'revertConfirmedAbandonment',
  partnerApproveForFiling = 'partnerApproveForFiling',
  reopen = 'reopen',
  closed = 'closed',
  done = 'done',
  documentUpload = 'documentUpload',
  onHold = 'onHold',
  resumeOnHold = 'resumeOnHold',
  sentForSignature = 'sentForSignature',
  filingExtension = 'filingExtension',
}

export const useInCommerceRequestTypeToDocumentType: Record<
  UseInCommerceRequestType,
  UseInCommerceRequestDocumentType
> = {
  [UseInCommerceRequestType.statementOfUse]: UseInCommerceRequestDocumentType.noticeOfAllowance,
  [UseInCommerceRequestType.declarationOfUse]:
    UseInCommerceRequestDocumentType.declarationOfUseNotice,
  [UseInCommerceRequestType.renewal]: UseInCommerceRequestDocumentType.renewalNotice,
}
